import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Camera,
  Check,
  CheckCheck,
  ChevronDown,
  Copy,
  Edit3,
  FileText,
  Forward,
  Image as ImageIcon,
  Info,
  Megaphone,
  MoreVertical,
  Music,
  PanelRightOpen,
  Plus,
  Search,
  Trash2,
  UserPlus,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { ProjectApi } from "api/projects/service";
import { BASE_URL, environment } from "utils/constant";
import {
  firebase,
  getToken,
  rtdb,
  sendPushNotification,
} from "../../../utils/firebase";
import MediaItem from "../../Project Dashboard/Snags/components/MediaItem";
import MessageInput from "../../Project Dashboard/Snags/components/MessageInput";
import LinkPreview from "../../Project Dashboard/Snags/components/LinkPreview";
import ImageZoomModal from "../Design/components/ImageZoomModal";
import { getImageUrl } from "../../Project Dashboard/Snags/hooks/useMediaUrl";
import NoMessage from "assets/empty state/NoMessage.svg";

const GENERAL_ROOM_ID = "general_updates";
const GENERAL_ROUTE_ID = "general-updates";
const NAME_COLORS = [
  "#25d366",
  "#53bdeb",
  "#ffb84d",
  "#ff7aa2",
  "#c084fc",
  "#7dd3fc",
  "#facc15",
  "#86efac",
];

const normalizeGeneralId = (roomId) =>
  roomId === GENERAL_ROUTE_ID ? GENERAL_ROOM_ID : roomId;

const roomPath = (room) => {
  const root =
    room.scope === "global"
      ? `global_snags-${environment}/${room.parentId}`
      : `site_snags-${environment}/${room.parentId}`;
  return `${root}/${normalizeGeneralId(room.roomId)}`;
};

const messagePath = (room) => `${roomPath(room)}/messages`;

const makeRoomKey = (scope, parentId, roomId) =>
  `${scope}:${parentId}:${normalizeGeneralId(roomId)}`;

const routeForRoom = (room) =>
  encodeURIComponent(makeRoomKey(room.scope, room.parentId, room.roomId));

const parseRoomParam = (param) => {
  if (!param) return null;
  const decoded = decodeURIComponent(param);
  if (decoded === GENERAL_ROUTE_ID || decoded === GENERAL_ROOM_ID) {
    return { scope: "global", roomId: GENERAL_ROOM_ID };
  }
  const [scope, parentId, ...roomParts] = decoded.split(":");
  if ((scope === "global" || scope === "site") && parentId && roomParts.length) {
    return { scope, parentId, roomId: normalizeGeneralId(roomParts.join(":")) };
  }
  return { scope: "global", roomId: normalizeGeneralId(decoded) };
};

const getDisplayTitle = (room) =>
  room?.roomId === GENERAL_ROOM_ID ? "General Updates" : room?.title || "Untitled";

const getAbsoluteUrl = (url) => {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return `${BASE_URL}${url.startsWith("/") ? "" : "/"}${url}`;
};

const getInitials = (name = "") => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "SM";
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
};

const formatTime = (ms) => {
  if (!ms) return "";
  return new Date(ms).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  });
};

const formatDateTime = (ms) => {
  if (!ms) return "";
  return new Date(ms).toLocaleString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const normalizeOrgMember = (member) => ({
  user_id: member?.user?.id,
  id: member?.id,
  first_name: member?.user?.first_name || "",
  last_name: member?.user?.last_name || "",
  email: member?.user?.email || "",
  userpic: getAbsoluteUrl(member?.user?.userpic),
  profession: member?.user?.profession,
  joined_as: member?.joined_as,
  is_admin: member?.is_admin,
});

const normalizeSiteMember = (member) => ({
  user_id: member?.user_id,
  id: member?.member_id,
  first_name: member?.first_name || "",
  last_name: member?.last_name || "",
  email: member?.email || "",
  userpic: getAbsoluteUrl(member?.userpic),
  profession: member?.joined_as,
  joined_as: member?.joined_as,
  is_admin: member?.is_admin || member?.org_admin,
});

const memberName = (member) =>
  `${member?.first_name || ""} ${member?.last_name || ""}`.trim() ||
  member?.email ||
  "Member";

const getMessagePreview = (message) => {
  if (!message) return "No messages yet";
  if (message.text) return message.text.replace(/\s+/g, " ").trim();
  if (message.has_media) {
    const media = Array.isArray(message.media) ? message.media : [message.media];
    const count = media.filter(Boolean).length;
    const type = media.find(Boolean)?.type || "media";
    return count > 1 ? `${count} ${type} files` : type;
  }
  return "Message";
};

const getMediaType = (media) => {
  const type = media?.type || "";
  if (type.includes("image")) return "images";
  if (type.includes("video")) return "videos";
  if (type.includes("audio")) return "audio";
  return "documents";
};

const isVisualMedia = (media) => {
  const type = media?.type || "";
  return type.includes("image") || type.includes("video");
};

const isVideoUrl = (url) => {
  if (!url) return false;
  return /\.(mp4|mov|webm|avi)(\?|$)/i.test(url);
};

const getMediaUrl = (mediaData) => mediaData?.image || mediaData?.file || null;

const CHAT_CACHE_NAME = "sitemaster-chat-assets-v1";
const CHAT_CACHE_MAX_AGE = 1000 * 60 * 60 * 24 * 7;
const memoryUrlCache = new Map();

const chatCacheRequest = (key) =>
  new Request(`${window.location.origin}/__sitemaster_chat_cache__/${encodeURIComponent(key)}`);

const openChatCache = async () => {
  if (!("caches" in window)) return null;
  return caches.open(CHAT_CACHE_NAME);
};

const objectUrlFromBlob = (key, blob) => {
  if (!blob) return null;
  if (memoryUrlCache.has(key)) return memoryUrlCache.get(key);
  const objectUrl = URL.createObjectURL(blob);
  memoryUrlCache.set(key, objectUrl);
  return objectUrl;
};

const getCachedAssetUrl = async (cacheKey, sourceUrl) => {
  if (!sourceUrl) return null;
  const key = `url:${cacheKey || sourceUrl}`;
  try {
    const cache = await openChatCache();
    if (!cache) return sourceUrl;
    const cachedResponse = await cache.match(chatCacheRequest(key));
    if (cachedResponse) {
      const cachedAt = Number(cachedResponse.headers.get("x-sm-cached-at") || 0);
      const cachedSource = cachedResponse.headers.get("x-sm-source-url");
      if (
        cachedAt &&
        Date.now() - cachedAt <= CHAT_CACHE_MAX_AGE &&
        cachedSource === sourceUrl
      ) {
        return objectUrlFromBlob(key, await cachedResponse.blob());
      }
    }
  } catch (error) {
    return sourceUrl;
  }

  try {
    const response = await fetch(sourceUrl);
    if (!response.ok) throw new Error("Failed to cache asset");
    const blob = await response.blob();
    const cache = await openChatCache();
    if (cache) {
      await cache.put(
        chatCacheRequest(key),
        new Response(blob, {
          headers: {
            "content-type": blob.type || "application/octet-stream",
            "x-sm-cached-at": String(Date.now()),
            "x-sm-source-url": sourceUrl,
          },
        }),
      );
    }
    return objectUrlFromBlob(key, blob);
  } catch (error) {
    return sourceUrl;
  }
};

const getCachedSnagMedia = async (mediaId) => {
  if (!mediaId) return null;
  return getImageUrl(mediaId);
};

const useCachedAssetUrl = (cacheKey, sourceUrl) => {
  const [cachedUrl, setCachedUrl] = useState(sourceUrl || null);

  useEffect(() => {
    let alive = true;
    if (!sourceUrl) {
      setCachedUrl(null);
      return () => {
        alive = false;
      };
    }
    setCachedUrl(sourceUrl);
    getCachedAssetUrl(cacheKey || sourceUrl, sourceUrl).then((url) => {
      if (alive) setCachedUrl(url || sourceUrl);
    });
    return () => {
      alive = false;
    };
  }, [cacheKey, sourceUrl]);

  return cachedUrl;
};

const useNearViewport = ({ rootMargin = "600px", once = true } = {}) => {
  const ref = useRef(null);
  const [isNear, setIsNear] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    if (!("IntersectionObserver" in window)) {
      setIsNear(true);
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNear(true);
          if (once) observer.disconnect();
        } else if (!once) {
          setIsNear(false);
        }
      },
      { root: null, rootMargin, threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin]);

  return [ref, isNear];
};

const messageMatchesQuery = (message, query) => {
  if (!query) return true;
  const haystack = [
    message.text,
    message.senderName,
    message.has_media ? "media" : "",
    ...(Array.isArray(message.media) ? message.media : [message.media])
      .filter(Boolean)
      .map((media) => media.type || ""),
  ]
    .join(" ")
    .toLowerCase();
  return haystack.includes(query.toLowerCase());
};

const startOfLocalDay = (date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const startOfLocalWeek = (date) => {
  const day = date.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const start = startOfLocalDay(date);
  start.setDate(start.getDate() + mondayOffset);
  return start;
};

const formatChatDateSeparator = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  const today = startOfLocalDay(new Date());
  const messageDay = startOfLocalDay(date);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (messageDay.getTime() === today.getTime()) return "Today";
  if (messageDay.getTime() === yesterday.getTime()) return "Yesterday";
  if (messageDay >= startOfLocalWeek(today)) {
    return date.toLocaleDateString("en-US", { weekday: "long" });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const shouldShowChatDateSeparator = (message, previousMessage) => {
  if (!message?.createdAt) return false;
  if (!previousMessage?.createdAt) return true;
  return (
    startOfLocalDay(new Date(message.createdAt)).getTime() !==
    startOfLocalDay(new Date(previousMessage.createdAt)).getTime()
  );
};

const ChatDateSeparator = ({ timestamp }) => {
  const label = formatChatDateSeparator(timestamp);
  if (!label) return null;
  return (
    <div className="my-4 flex items-center justify-center px-6">
      <div className="h-px flex-1 bg-gray-200" />
      <span className="mx-3 rounded-full border border-gray-200 bg-white px-4 py-1 text-xs font-semibold text-gray-600 shadow-sm">
        {label}
      </span>
      <div className="h-px flex-1 bg-gray-200" />
    </div>
  );
};

const isOwnerOrAdmin = (members, currentUserId) =>
  members.some(
    (member) =>
      member.user_id === currentUserId &&
      (member.is_admin || String(member.joined_as || "").toLowerCase() === "owner"),
  );

const Avatar = ({
  src,
  name,
  className = "",
  rounded = "rounded-full",
  cacheKey,
}) => {
  const [avatarRef, isNearViewport] = useNearViewport();
  const cachedSrc = useCachedAssetUrl(
    cacheKey || src,
    isNearViewport ? src : null,
  );

  return (
    <div
      ref={avatarRef}
      className={`shrink-0 overflow-hidden bg-sm-primary text-white flex items-center justify-center font-bold ${rounded} ${className}`}
    >
      {cachedSrc ? (
        <img src={cachedSrc} alt={name || "Avatar"} className="h-full w-full object-cover" />
      ) : (
        <span>{getInitials(name)}</span>
      )}
    </div>
  );
};

const RoomIcon = ({ room, size = "h-12 w-12" }) => {
  const [iconUrl, setIconUrl] = useState(null);
  const [iconRef, isNearViewport] = useNearViewport();

  useEffect(() => {
    if (!isNearViewport) return undefined;
    let alive = true;
    const fetchIcon = async () => {
      if (!room?.icon) {
        setIconUrl(null);
        return;
      }
      try {
        const data = await getCachedSnagMedia(room.icon);
        if (alive) setIconUrl(data?.image || data?.file || null);
      } catch (error) {
        if (alive) setIconUrl(null);
      }
    };
    fetchIcon();
    return () => {
      alive = false;
    };
  }, [isNearViewport, room?.icon]);

  if (iconUrl) {
    return (
      <Avatar
        src={iconUrl}
        name={getDisplayTitle(room)}
        className={size}
        cacheKey={`room-icon:${room.icon}`}
      />
    );
  }

  return (
    <div
      ref={iconRef}
      className={`${size} rounded-full bg-sm-primary text-white flex items-center justify-center shadow-inner ring-1 ring-sm-primary/10`}
    >
      {room?.roomId === GENERAL_ROOM_ID ? (
        <Megaphone size={22} />
      ) : (
        <span className="text-sm font-bold">{getInitials(getDisplayTitle(room))}</span>
      )}
    </div>
  );
};

const ParentRoomStack = ({ room }) => (
  <div className="relative h-14 w-16 shrink-0" title={`${room.parentName} chatrooms`}>
    <Avatar
      src={room.parentImage}
      name={room.parentName}
      className="absolute left-0 top-1 h-10 w-10 text-xs"
      rounded="rounded-xl"
      cacheKey={`${room.scope}-parent:${room.parentId}`}
    />
    <div className="absolute bottom-0 right-0">
      <RoomIcon room={room} size="h-10 w-10" />
    </div>
  </div>
);

const ChatMediaItem = ({
  mediaItem,
  gallery,
  galleryIndex,
  onOpenGallery,
  eager = false,
}) => {
  const [mediaData, setMediaData] = useState(null);
  const [loading, setLoading] = useState(Boolean(mediaItem?.mediaId));
  const [loadRef, isNearViewport] = useNearViewport();

  useEffect(() => {
    if (!isNearViewport && !eager) return undefined;
    let alive = true;
    const fetchMedia = async () => {
      if (!mediaItem?.mediaId) {
        setMediaData(mediaItem);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await getCachedSnagMedia(mediaItem.mediaId);
        if (alive) setMediaData(data || mediaItem);
      } catch (error) {
        if (alive) setMediaData(mediaItem);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchMedia();
    return () => {
      alive = false;
    };
  }, [eager, isNearViewport, mediaItem]);

  const url = getMediaUrl(mediaData) || mediaItem?.url;
  const type = mediaData?.type || mediaItem?.type || "";

  if (!isNearViewport && !eager) {
    return <div ref={loadRef} className="h-32 w-32 rounded-lg bg-gray-100" />;
  }

  if (loading) {
    return <div ref={loadRef} className="h-32 w-32 animate-pulse rounded-lg bg-gray-200" />;
  }

  if (isVisualMedia({ type }) && url) {
    return (
      <button
        ref={loadRef}
        type="button"
        onClick={() => onOpenGallery(gallery, galleryIndex)}
        className="overflow-hidden rounded-lg border border-gray-200 bg-white"
      >
        {type.includes("video") || isVideoUrl(url) ? (
          <video src={url} className="h-36 max-w-64 object-cover" />
        ) : (
          <img src={url} alt="Shared media" className="h-36 max-w-64 object-cover" />
        )}
      </button>
    );
  }

  return (
    <div ref={loadRef}>
      <MediaItem
        mediaItem={mediaItem}
        setShowImagePreview={() => {}}
        setSelectedMedia={() => {}}
      />
    </div>
  );
};

const CollageTile = ({ media, className = "", children, eager = false }) => {
  const [mediaData, setMediaData] = useState(null);
  const [loadRef, isNearViewport] = useNearViewport();

  useEffect(() => {
    if (!isNearViewport && !eager) return undefined;
    let alive = true;
    const fetchMedia = async () => {
      if (!media?.mediaId) {
        setMediaData(media);
        return;
      }
      const data = await getCachedSnagMedia(media.mediaId);
      if (alive) setMediaData(data || media);
    };
    fetchMedia();
    return () => {
      alive = false;
    };
  }, [eager, isNearViewport, media]);

  const url = getMediaUrl(mediaData) || media?.url;
  const type = mediaData?.type || media?.type || "";

  if (!isNearViewport && !eager) {
    return <div ref={loadRef} className={`animate-pulse bg-gray-200 ${className}`} />;
  }

  return (
    <div ref={loadRef} className={`relative overflow-hidden bg-gray-200 ${className}`}>
      {url ? (
        type.includes("video") || isVideoUrl(url) ? (
          <video src={url} className="h-full w-full object-cover" />
        ) : (
          <img src={url} alt="Shared media" className="h-full w-full object-cover" />
        )
      ) : (
        <div className="h-full w-full animate-pulse bg-gray-200" />
      )}
      {children}
    </div>
  );
};

const MediaCollage = ({ mediaItems, onClick, eager = false }) => {
  const hiddenCount = mediaItems.length - 3;

  return (
    <button
      type="button"
      onClick={onClick}
      className="grid h-56 w-64 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
      style={{ gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr" }}
    >
      {mediaItems.slice(0, 3).map((media, index) => (
        <CollageTile
          key={media.mediaId || index}
          media={media}
          eager={eager}
          className={index === 0 ? "border-r border-b border-white" : "border-b border-white"}
        />
      ))}
      <CollageTile media={mediaItems[3]} eager={eager} className="border-l border-white">
        {hiddenCount > 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/55 text-2xl font-bold text-white">
            +{hiddenCount}
          </div>
        )}
      </CollageTile>
    </button>
  );
};

const ChatMessage = ({
  message,
  previousMessage,
  currentUser,
  members,
  readBy,
  participantIds,
  selectionMode,
  selected,
  onToggleSelect,
  menuOpenFor,
  setMenuOpenFor,
  onCopy,
  onDelete,
  onEdit,
  onReply,
  onForward,
  onOpenGallery,
  eagerMedia,
}) => {
  const isMine = message.senderId === currentUser?.user;
  const sender = members.find((member) => member.user_id === message.senderId);
  const showSender =
    !isMine &&
    (previousMessage?.senderId !== message.senderId ||
      new Date(previousMessage?.createdAt || 0).toDateString() !==
        new Date(message.createdAt || 0).toDateString());
  const nameColor =
    NAME_COLORS[
      Math.abs(Number(message.senderId) || 0) % NAME_COLORS.length
    ];
  const firstUrl = (message.text || "").match(/(https?:\/\/[^\s]+)/)?.[0];
  const otherParticipants = participantIds.filter((id) => id !== message.senderId);
  const seenByAll =
    otherParticipants.length === 0 ||
    otherParticipants.every(
      (id) => (readBy?.[id]?.lastReadAt || 0) >= (message.createdAt || 0),
    );

  const textParts = (message.text || "").split(/(https?:\/\/[^\s]+)/g);

  return (
    <div
      className={`group flex gap-2 px-5 py-1 ${isMine ? "justify-end" : "justify-start"} ${
        selected ? "bg-card" : ""
      }`}
      onClick={() => selectionMode && onToggleSelect(message.id)}
    >
      {selectionMode && (
        <button
          type="button"
          className={`mt-4 h-5 w-5 rounded border flex items-center justify-center ${
            selected ? "bg-sm-primary border-sm-primary text-white" : "border-gray-300"
          }`}
          onClick={(event) => {
            event.stopPropagation();
            onToggleSelect(message.id);
          }}
          aria-label="Select message"
        >
          {selected && <Check size={14} />}
        </button>
      )}

      {!isMine && (
        <Avatar
          src={sender?.userpic}
          name={memberName(sender) || message.senderName}
          className="mt-5 h-8 w-8 text-[10px]"
          cacheKey={`user:${message.senderId}`}
        />
      )}

      <div
        className={`relative max-w-[72%] rounded-lg px-3 py-2 pr-8 shadow-sm ${
          isMine
            ? "bg-[#eadff2] text-gray-900"
            : "bg-white text-gray-900"
        }`}
      >
        {!selectionMode && (
          <div className="absolute right-1 top-1 z-20 opacity-0 transition-opacity group-hover:opacity-100">
            <button
              type="button"
              className="rounded-full p-1 text-gray-500 hover:bg-gray-100 hover:text-sm-primary"
              onClick={(event) => {
                event.stopPropagation();
                setMenuOpenFor((prev) => (prev === message.id ? null : message.id));
              }}
              aria-label="Message actions"
            >
              <ChevronDown size={17} />
            </button>
            {menuOpenFor === message.id && (
              <MessageMenu
                align="right"
                isMine={isMine}
                onCopy={() => onCopy([message])}
                onDelete={() => onDelete([message])}
                onEdit={() => onEdit(message)}
                onReply={() => onReply(message)}
                onForward={() => onForward([message])}
                onClose={() => setMenuOpenFor(null)}
              />
            )}
          </div>
        )}

        {showSender && (
          <div className="mb-1 text-xs font-semibold" style={{ color: nameColor }}>
            {message.senderName || memberName(sender)}
          </div>
        )}

        {message.replyTo && (
          <div className="mb-2 rounded border-l-4 border-sm-primary bg-gray-50 px-2 py-1 text-xs text-gray-600">
            <div className="font-semibold">{message.replyTo.senderName}</div>
            <div className="line-clamp-2">
              {message.replyTo.text ||
                (message.replyTo.has_media ? "Media message" : "Message")}
            </div>
          </div>
        )}

        {message.has_media && (
          <div className="mb-2 flex max-w-md flex-wrap gap-2">
            {(() => {
              const messageMedia = (Array.isArray(message.media)
                ? message.media
                : [message.media]
              ).filter(Boolean);
              const visualGallery = messageMedia.filter(isVisualMedia);
              const nonVisualMedia = messageMedia.filter((media) => !isVisualMedia(media));

              if (visualGallery.length > 2) {
                return (
                  <>
                    <MediaCollage
                      mediaItems={visualGallery}
                      onClick={() => onOpenGallery(visualGallery, 0)}
                      eager={eagerMedia}
                    />
                    {nonVisualMedia.map((mediaItem, index) => (
                      <ChatMediaItem
                        key={mediaItem.mediaId || index}
                        mediaItem={mediaItem}
                        gallery={visualGallery}
                        galleryIndex={0}
                        onOpenGallery={onOpenGallery}
                        eager={eagerMedia}
                      />
                    ))}
                  </>
                );
              }

              return messageMedia.map((mediaItem, index) => (
                <ChatMediaItem
                  key={mediaItem.mediaId || index}
                  mediaItem={mediaItem}
                  gallery={visualGallery}
                  galleryIndex={Math.max(
                    0,
                    visualGallery.findIndex((media) => media === mediaItem),
                  )}
                  onOpenGallery={onOpenGallery}
                  eager={eagerMedia}
                />
              ));
            })()}
          </div>
        )}

        {message.text && (
          <div className="whitespace-pre-wrap break-words text-[15px] leading-5">
            {textParts.map((part, index) =>
              /^https?:\/\//.test(part) ? (
                <a
                  key={`${message.id}-${index}`}
                  href={part}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm-primary underline"
                  onClick={(event) => event.stopPropagation()}
                >
                  {part}
                </a>
              ) : (
                <React.Fragment key={`${message.id}-${index}`}>{part}</React.Fragment>
              ),
            )}
            {firstUrl && <LinkPreview url={firstUrl} />}
          </div>
        )}

        <div className="mt-1 flex items-center justify-end gap-1 text-[11px] text-gray-500">
          <span>{formatTime(message.createdAt)}</span>
          {isMine &&
            (seenByAll ? (
              <CheckCheck size={15} className="text-blue-500" />
            ) : (
              <Check size={15} />
            ))}
        </div>
      </div>
    </div>
  );
};

const MessageMenu = ({
  isMine,
  onCopy,
  onDelete,
  onEdit,
  onReply,
  onForward,
  onClose,
  align = "left",
}) => (
  <div
    className={`absolute top-8 z-40 w-48 overflow-hidden rounded-xl border border-gray-200 bg-white py-2 text-sm text-gray-800 shadow-2xl ${
      align === "right" ? "right-0" : "left-0"
    }`}
  >
    {[
      { label: "Reply", icon: PanelRightOpen, action: onReply },
      { label: "Forward", icon: Forward, action: onForward },
      { label: "Copy", icon: Copy, action: onCopy },
      isMine ? { label: "Edit", icon: Edit3, action: onEdit } : null,
      isMine ? { label: "Delete", icon: Trash2, action: onDelete, danger: true } : null,
    ].filter(Boolean).map((item) => {
      const Icon = item.icon;
      return (
        <button
          key={item.label}
          type="button"
          className={`flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 ${
            item.danger ? "text-red-600" : ""
          }`}
          onClick={() => {
            item.action?.();
            onClose?.();
          }}
        >
          <Icon size={16} />
          <span>{item.label}</span>
        </button>
      );
    })}
  </div>
);

const Chats = () => {
  const { snagRoomId } = useParams();
  const navigate = useNavigate();
  const organization = JSON.parse(localStorage.getItem("organization") || "{}");
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
  const currentUserId = currentUser?.user;

  const [projects, setProjects] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [siteMembersBySite, setSiteMembersBySite] = useState({});
  const [roomsByKey, setRoomsByKey] = useState({});
  const [activeRoomKey, setActiveRoomKey] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [parentRoomMenu, setParentRoomMenu] = useState(false);
  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  const [menuOpenFor, setMenuOpenFor] = useState(null);
  const [replyToMessage, setReplyToMessage] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [showInfo, setShowInfo] = useState(false);
  const [showCreateRoomModal, setShowCreateRoomModal] = useState(false);
  const [createRoomScope, setCreateRoomScope] = useState("global");
  const [createRoomParentId, setCreateRoomParentId] = useState("");
  const [createRoomTitle, setCreateRoomTitle] = useState("");
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedMessageIds, setSelectedMessageIds] = useState([]);
  const [forwardMessages, setForwardMessages] = useState([]);
  const [forwardTargetKeys, setForwardTargetKeys] = useState([]);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [chatSearchOpen, setChatSearchOpen] = useState(false);
  const [chatSearchQuery, setChatSearchQuery] = useState("");
  const [chatSearchIndex, setChatSearchIndex] = useState(0);
  const [editingMessage, setEditingMessage] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
  const [mediaViewerTab, setMediaViewerTab] = useState("images");
  const [zoomGallery, setZoomGallery] = useState([]);
  const [zoomIndex, setZoomIndex] = useState(0);

  const messagesContainerRef = useRef(null);
  const messageRefs = useRef({});
  const iconUploadInputRef = useRef(null);
  const autoScrollRef = useRef(true);
  const forceScrollToBottomRef = useRef(true);

  const rooms = useMemo(() => {
    return Object.values(roomsByKey).sort((a, b) => {
      const aTime = a.latestAt || a.createdAt || 0;
      const bTime = b.latestAt || b.createdAt || 0;
      return bTime - aTime;
    });
  }, [roomsByKey]);

  const activeRoom = useMemo(
    () => rooms.find((room) => room.key === activeRoomKey) || null,
    [activeRoomKey, rooms],
  );

  const activeMembers = useMemo(() => {
    if (!activeRoom) return [];
    return activeRoom.scope === "global"
      ? orgMembers
      : siteMembersBySite[activeRoom.parentId] || [];
  }, [activeRoom, orgMembers, siteMembersBySite]);

  const activeAdmin = useMemo(
    () => isOwnerOrAdmin(activeMembers, currentUserId),
    [activeMembers, currentUserId],
  );

  const participantIds = useMemo(() => {
    if (!activeRoom) return [];
    if (Array.isArray(activeRoom.members) && activeRoom.members.length) {
      return activeRoom.members;
    }
    return activeMembers.map((member) => member.user_id).filter(Boolean);
  }, [activeMembers, activeRoom]);

  const activeReadPath =
    activeRoom && currentUserId ? `${roomPath(activeRoom)}/read_by/${currentUserId}` : null;

  const visibleRooms = useMemo(() => {
    return rooms.filter((room) => {
      if (!currentUserId) return true;
      if (room.roomId === GENERAL_ROOM_ID) return true;
      const members =
        room.scope === "global"
          ? orgMembers
          : siteMembersBySite[room.parentId] || [];
      if (isOwnerOrAdmin(members, currentUserId)) return true;
      return !Array.isArray(room.members) || room.members.length === 0
        ? true
        : room.members.includes(currentUserId);
    });
  }, [currentUserId, orgMembers, rooms, siteMembersBySite]);

  const filteredRooms = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return visibleRooms;
    return visibleRooms.filter((room) => {
      const latest = getMessagePreview(room.latestMessage).toLowerCase();
      return (
        getDisplayTitle(room).toLowerCase().includes(query) ||
        room.parentName.toLowerCase().includes(query) ||
        latest.includes(query) ||
        (room.latestMessage?.senderName || "").toLowerCase().includes(query)
      );
    });
  }, [searchQuery, visibleRooms]);

  const selectedMessages = useMemo(() => {
    if (!activeRoom) return [];
    return activeRoom.messages.filter((message) =>
      selectedMessageIds.includes(message.id),
    );
  }, [activeRoom, selectedMessageIds]);

  const mediaByType = useMemo(() => {
    const grouped = { images: [], videos: [], documents: [], audio: [] };
    if (!activeRoom) return grouped;
    activeRoom.messages.forEach((message) => {
      if (!message.has_media) return;
      const mediaList = Array.isArray(message.media) ? message.media : [message.media];
      mediaList.filter(Boolean).forEach((media) => {
        grouped[getMediaType(media)].push({ ...media, messageId: message.id });
      });
    });
    return grouped;
  }, [activeRoom]);

  const latestMediaMessageIds = useMemo(() => {
    const ids = new Set();
    if (!activeRoom) return ids;
    let mediaCount = 0;
    for (let i = activeRoom.messages.length - 1; i >= 0; i -= 1) {
      const message = activeRoom.messages[i];
      if (!message?.has_media) continue;
      const count = (Array.isArray(message.media) ? message.media : [message.media])
        .filter(Boolean).length;
      if (!count) continue;
      ids.add(message.id);
      mediaCount += count;
      if (mediaCount >= 20) break;
    }
    return ids;
  }, [activeRoom]);

  const allRoomMedia = useMemo(
    () => [
      ...mediaByType.images,
      ...mediaByType.videos,
      ...mediaByType.documents,
      ...mediaByType.audio,
    ],
    [mediaByType],
  );

  const chatSearchResults = useMemo(() => {
    const query = chatSearchQuery.trim();
    if (!activeRoom || !query) return [];
    return activeRoom.messages.filter((message) => messageMatchesQuery(message, query));
  }, [activeRoom, chatSearchQuery]);

  useEffect(() => {
    setChatSearchIndex(0);
  }, [chatSearchQuery, activeRoomKey]);

  useEffect(() => {
    if (!chatSearchQuery.trim() || !chatSearchResults.length) return;
    const first = chatSearchResults[0];
    requestAnimationFrame(() => {
      messageRefs.current[first.id]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }, [chatSearchQuery, chatSearchResults]);

  useEffect(() => {
    if (createRoomScope === "global") {
      setCreateRoomParentId(String(organization?.id || ""));
    } else if (!createRoomParentId && projects.length) {
      setCreateRoomParentId(String(projects[0].id));
    }
  }, [createRoomParentId, createRoomScope, organization?.id, projects]);

  useEffect(() => {
    const fetchBaseData = async () => {
      try {
        const [projectResponse, memberResponse] = await Promise.all([
          ProjectApi.listSitesByUser(),
          fetch(`${BASE_URL}/org/organization/${organization?.id}/members/`, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${localStorage.getItem("access")}`,
            },
          }).then((response) => response.json()),
        ]);

        const userProjects = projectResponse?.data.filter((item) => item.organization === organization?.id) || [];
        setProjects(userProjects);
        setOrgMembers((memberResponse?.data || []).map(normalizeOrgMember));

        const siteMembers = await Promise.all(
          userProjects.map(async (site) => {
            try {
              const response = await fetch(
                `${BASE_URL}/team/list-site-members/${site.id}/`,
                {
                  method: "GET",
                  headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${localStorage.getItem("access")}`,
                  },
                },
              );
              const data = await response.json();
              return [site.id, (data?.data || []).map(normalizeSiteMember)];
            } catch (error) {
              return [site.id, []];
            }
          }),
        );
        setSiteMembersBySite(Object.fromEntries(siteMembers));
      } catch (error) {
        toast.error(error?.message || "Failed to load chats");
      }
    };

    if (organization?.id) fetchBaseData();
  }, [organization?.id]);

  useEffect(() => {
    if (!organization?.id) return undefined;
    const refs = [];
    const parentDescriptors = [
      {
        scope: "global",
        id: organization.id,
        name: organization.organization_name || "SiteMaster",
        image: getAbsoluteUrl(organization.logo_image_file),
      },
      ...projects.map((site) => ({
        scope: "site",
        id: site.id,
        name: site.site_name || `Project ${site.id}`,
        image: getAbsoluteUrl(site.image_file || site.image_url),
        site,
      })),
    ];

    parentDescriptors.forEach((parent) => {
      const root =
        parent.scope === "global"
          ? `global_snags-${environment}/${parent.id}`
          : `site_snags-${environment}/${parent.id}`;
      const ref = rtdb.ref(root);
      const handler = (snapshot) => {
        const nextRooms = {};
        let hasGeneral = false;

        snapshot.forEach((child) => {
          const roomId = normalizeGeneralId(child.key);
          const raw = child.val() || {};
          if (roomId === GENERAL_ROOM_ID) hasGeneral = true;
          const messages = Object.entries(raw.messages || {})
            .map(([id, value]) => ({ id, ...value }))
            .sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
          const latestMessage = messages[messages.length - 1] || null;
          const key = makeRoomKey(parent.scope, parent.id, roomId);

          nextRooms[key] = {
            key,
            scope: parent.scope,
            parentId: parent.id,
            parentName: parent.name,
            parentImage: parent.image,
            parentSite: parent.site,
            roomId,
            title: roomId === GENERAL_ROOM_ID ? "General Updates" : raw.title,
            icon: raw.icon,
            createdAt: raw.createdAt || latestMessage?.createdAt || 0,
            createdBy: raw.createdBy,
            creatorName: raw.creatorName,
            members: Array.isArray(raw.members) ? raw.members : null,
            messages,
            readBy: raw.read_by || {},
            latestMessage,
            latestAt: latestMessage?.createdAt || raw.createdAt || 0,
          };
        });

        if (!hasGeneral) {
          const key = makeRoomKey(parent.scope, parent.id, GENERAL_ROOM_ID);
          nextRooms[key] = {
            key,
            scope: parent.scope,
            parentId: parent.id,
            parentName: parent.name,
            parentImage: parent.image,
            parentSite: parent.site,
            roomId: GENERAL_ROOM_ID,
            title: "General Updates",
            createdAt: 0,
            members: null,
            messages: [],
            readBy: {},
            latestMessage: null,
            latestAt: 0,
          };
        }

        setRoomsByKey((prev) => {
          const scopedPrefix = `${parent.scope}:${parent.id}:`;
          const cleaned = Object.fromEntries(
            Object.entries(prev).filter(([key]) => !key.startsWith(scopedPrefix)),
          );
          return { ...cleaned, ...nextRooms };
        });
      };
      ref.on("value", handler);
      refs.push([ref, handler]);
    });

    return () => {
      refs.forEach(([ref, handler]) => ref.off("value", handler));
    };
  }, [
    organization?.id,
    organization?.logo_image_file,
    organization?.organization_name,
    projects,
  ]);

  useEffect(() => {
    if (!visibleRooms.length) return;
    const parsed = parseRoomParam(snagRoomId);
    const requestedKey = parsed
      ? visibleRooms.find((room) => {
          const roomIdMatches = room.roomId === normalizeGeneralId(parsed.roomId);
          const scopeMatches = !parsed.scope || room.scope === parsed.scope;
          const parentMatches =
            !parsed.parentId || String(room.parentId) === String(parsed.parentId);
          return roomIdMatches && scopeMatches && parentMatches;
        })?.key
      : null;
    const nextKey =
      requestedKey ||
      (activeRoomKey && visibleRooms.some((room) => room.key === activeRoomKey)
        ? activeRoomKey
        : visibleRooms[0].key);

    if (nextKey && nextKey !== activeRoomKey) {
      setActiveRoomKey(nextKey);
    }
  }, [activeRoomKey, snagRoomId, visibleRooms]);

  useEffect(() => {
    if (!activeReadPath || !currentUserId) return;
    rtdb.ref(activeReadPath).set({
      lastReadAt: firebase.database.ServerValue.TIMESTAMP,
      userId: currentUserId,
    });
  }, [activeReadPath, currentUserId]);

  useEffect(() => {
    setSelectedMessageIds([]);
    setSelectionMode(false);
    setReplyToMessage(null);
    setHeaderMenuOpen(false);
    setParentRoomMenu(false);
    forceScrollToBottomRef.current = true;
  }, [activeRoomKey]);

  const scrollToBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    container.scrollTop = container.scrollHeight;
  };

  useLayoutEffect(() => {
    if (!activeRoom) return;
    const shouldScroll = forceScrollToBottomRef.current || autoScrollRef.current;
    if (!shouldScroll) return;
    requestAnimationFrame(() => {
      scrollToBottom();
      forceScrollToBottomRef.current = false;
    });
  }, [activeRoom, activeRoom?.messages.length]);

  const handleMessagesScroll = () => {
    const container = messagesContainerRef.current;
    if (!container) return;
    const distanceFromBottom =
      container.scrollHeight - container.scrollTop - container.clientHeight;
    autoScrollRef.current = distanceFromBottom < 120;
    setShowScrollToBottom(distanceFromBottom > 240);
  };

  const goToChatSearchResult = (direction) => {
    if (!chatSearchResults.length) return;
    const nextIndex =
      direction === "next"
        ? (chatSearchIndex + 1) % chatSearchResults.length
        : (chatSearchIndex - 1 + chatSearchResults.length) %
          chatSearchResults.length;
    setChatSearchIndex(nextIndex);
    const nextMessage = chatSearchResults[nextIndex];
    messageRefs.current[nextMessage.id]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  const openRoom = (room) => {
    navigate(`/dashboard/global-communication/${routeForRoom(room)}`);
    setActiveRoomKey(room.key);
  };

  const calculateUnread = (room) => {
    const lastReadAt = room.readBy?.[currentUserId]?.lastReadAt || 0;
    return room.messages.filter(
      (message) =>
        (message.createdAt || 0) > lastReadAt &&
        message.senderId !== currentUserId,
    ).length;
  };

  const pushInAppNotification = async ({ title, message, targetIds, room }) => {
    try {
      await rtdb.ref(`notifications-${environment}/${organization.id}`).push({
        title,
        message,
        site_id: room.scope === "site" ? room.parentId : undefined,
        date: new Date().toISOString(),
        read: false,
        type: "snag_message",
        metadata: {
          snag_id: room.roomId,
          type: "new_message",
        },
        target_ids: targetIds,
        actor_id: currentUserId,
        module: "Chats",
      });
    } catch (error) {
      console.error("Failed to push in-app notification:", error);
    }
  };

  const sendChatMessage = async (text) => {
    if (!activeRoom || (!text.trim() && mediaFiles.length === 0)) return;

    let mediaArray = [];
    let firstImage = null;
    if (mediaFiles.length) {
      setIsUploading(true);
      try {
        mediaArray = await Promise.all(
          mediaFiles.map(async (file) => {
            const formData = new FormData();
            formData.append("site_id", activeRoom.parentId);
            formData.append("snag_id", activeRoom.roomId);

            let type = "document";
            if (file.type.startsWith("image/")) type = "image";
            else if (file.type.startsWith("video/")) type = "video";
            else if (file.type.startsWith("audio/")) type = "audio";

            formData.append("type", type);
            formData.append(type === "image" ? "image" : "file", file);

            const response = await fetch(`${BASE_URL}/core/uploadsnagobjects/`, {
              method: "POST",
              headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
              body: formData,
            });
            const data = await response.json();
            if (type === "image" && !firstImage) firstImage = data?.data?.image;
            return {
              url: data?.data?.image || data?.data?.file,
              type,
              mediaId: data?.data?.id,
            };
          }),
        );
      } finally {
        setIsUploading(false);
        setMediaFiles([]);
      }
    }

    const message = {
      text,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      senderId: currentUserId,
      senderName:
        `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() ||
        "Anonymous",
      media: mediaArray.length ? mediaArray : null,
      has_media: mediaArray.length > 0,
      replyTo: replyToMessage || null,
    };

    await rtdb.ref(messagePath(activeRoom)).push(message);
    setReplyToMessage(null);
    setTimeout(scrollToBottom, 100);

    const targetIds = participantIds.length
      ? participantIds
      : activeMembers.map((member) => member.user_id).filter(Boolean);
    const title = `${getDisplayTitle(activeRoom)} - ${activeRoom.parentName}`;
    const body = `${message.senderName}: ${text}${mediaArray.length ? " (media)" : ""}`;
    const data = {
      room_id: makeRoomKey(activeRoom.scope, activeRoom.parentId, activeRoom.roomId),
      snag_id: activeRoom.roomId,
      site_id: activeRoom.scope === "site" ? activeRoom.parentId : undefined,
      url: `sitemaster://chat-room?roomId=${activeRoom.scope}:${activeRoom.parentId}:${activeRoom.roomId}&roomName=${getDisplayTitle(activeRoom)}`,
    };

    targetIds.forEach(async (uid) => {
      if (uid === currentUserId) return;
      const token = await getToken(uid);
      if (token?.token) {
        sendPushNotification(
          token.token,
          title,
          body,
          data,
          Boolean(firstImage),
          firstImage,
        );
      }
    });

    await pushInAppNotification({
      title,
      message: body,
      targetIds,
      room: activeRoom,
    });
  };

  const toggleMessageSelection = (messageId) => {
    setSelectedMessageIds((prev) =>
      prev.includes(messageId)
        ? prev.filter((id) => id !== messageId)
        : [...prev, messageId],
    );
  };

  const copyMessages = async (messagesToCopy) => {
    const text = messagesToCopy
      .map((message) => message.text)
      .filter(Boolean)
      .join("\n\n");
    if (!text) {
      toast.info("No text to copy");
      return;
    }
    await navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  const deleteMessages = async (messagesToDelete) => {
    if (!activeRoom || !messagesToDelete.length) return;
    await Promise.all(
      messagesToDelete.map((message) =>
        rtdb.ref(`${messagePath(activeRoom)}/${message.id}`).remove(),
      ),
    );
    setSelectedMessageIds([]);
    setSelectionMode(false);
    toast.success("Message deleted");
  };

  const openForwardModal = (messagesToForward) => {
    setForwardMessages(messagesToForward);
    setForwardTargetKeys([]);
  };

  const openVisualGallery = async (mediaItems, startIndex = 0) => {
    const visuals = (mediaItems || []).filter(isVisualMedia);
    if (!visuals.length) return;
    try {
      const resolved = await Promise.all(
        visuals.map(async (media) => {
          const data = media.mediaId ? await getCachedSnagMedia(media.mediaId) : media;
          return {
            ...media,
            url: getMediaUrl(data) || media.url,
            type: data?.type || media.type,
          };
        }),
      );
      setZoomGallery(resolved.filter((media) => media.url));
      setZoomIndex(Math.max(0, Math.min(startIndex, resolved.length - 1)));
    } catch (error) {
      toast.error("Failed to open media");
    }
  };

  const forwardSelectedMessages = async () => {
    if (!forwardMessages.length || !forwardTargetKeys.length) return;
    const targetRooms = rooms.filter((room) => forwardTargetKeys.includes(room.key));
    const sortedMessages = [...forwardMessages].sort(
      (a, b) => (a.createdAt || 0) - (b.createdAt || 0),
    );

    await Promise.all(
      targetRooms.flatMap((room) =>
        sortedMessages.map((message) =>
          rtdb.ref(messagePath(room)).push({
            text: message.text || "",
            createdAt: firebase.database.ServerValue.TIMESTAMP,
            senderId: currentUserId,
            senderName:
              `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() ||
              "Anonymous",
            media: message.media || null,
            has_media: Boolean(message.has_media),
            forwarded: true,
            forwardedFrom: {
              roomId: activeRoom?.roomId,
              roomTitle: activeRoom ? getDisplayTitle(activeRoom) : "",
              senderName: message.senderName,
            },
          }),
        ),
      ),
    );

    setForwardMessages([]);
    setForwardTargetKeys([]);
    setSelectedMessageIds([]);
    setSelectionMode(false);
    toast.success("Forwarded");
  };

  const saveRoomMembers = async () => {
    if (!activeRoom) return;
    await rtdb
      .ref(`${roomPath(activeRoom)}/members`)
      .set(selectedMemberIds.length ? selectedMemberIds : null);
    setShowMemberModal(false);
    toast.success("Members updated");
  };

  const deleteActiveRoom = async () => {
    if (!activeRoom || activeRoom.roomId === GENERAL_ROOM_ID || !activeAdmin) return;
    await rtdb.ref(roomPath(activeRoom)).remove();
    toast.success("Chatroom deleted");
  };

  const createRoom = async () => {
    const title = createRoomTitle.trim();
    if (!title) return;
    const parentId =
      createRoomScope === "global" ? organization.id : Number(createRoomParentId);
    if (!parentId) {
      toast.error("Please select a project");
      return;
    }
    const root =
      createRoomScope === "global"
        ? `global_snags-${environment}/${parentId}`
        : `site_snags-${environment}/${parentId}`;
    const newRef = await rtdb.ref(root).push({
      title,
      createdAt: firebase.database.ServerValue.TIMESTAMP,
      createdBy: currentUserId || "unknown",
      creatorName:
        `${currentUser?.first_name || ""} ${currentUser?.last_name || ""}`.trim() ||
        "Anonymous",
      members: null,
      messages: {},
    });
    setShowCreateRoomModal(false);
    setCreateRoomTitle("");
    setTimeout(() => {
      const key = makeRoomKey(createRoomScope, parentId, newRef.key);
      const next = Object.values(roomsByKey).find((room) => room.key === key);
      if (next) openRoom(next);
    }, 300);
  };

  const handleRoomIconUpload = async (file) => {
    if (!activeRoom || activeRoom.roomId === GENERAL_ROOM_ID || !file) return;
    try {
      const formData = new FormData();
      formData.append("snag_id", GENERAL_ROOM_ID);
      formData.append("type", "image");
      formData.append("image", file);
      const response = await fetch(`${BASE_URL}/core/uploadsnagobjects/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("access")}` },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok || !data?.status || !data?.data?.id) {
        throw new Error(data?.message || "Failed to upload icon");
      }
      await rtdb.ref(`${roomPath(activeRoom)}/icon`).set(data.data.id);
      toast.success("Chatroom icon updated");
    } catch (error) {
      toast.error(error?.message || "Failed to update chatroom icon");
    } finally {
      if (iconUploadInputRef.current) iconUploadInputRef.current.value = "";
    }
  };

  const startEditMessage = (message) => {
    if (message.senderId !== currentUserId) return;
    setEditingMessage(message);
    setEditingText(message.text || "");
  };

  const saveEditedMessage = async () => {
    if (!activeRoom || !editingMessage) return;
    await rtdb.ref(`${messagePath(activeRoom)}/${editingMessage.id}`).update({
      text: editingText,
      editedAt: firebase.database.ServerValue.TIMESTAMP,
    });
    setEditingMessage(null);
    setEditingText("");
    toast.success("Message updated");
  };

  const openMemberModal = () => {
    if (!activeRoom || activeRoom.roomId === GENERAL_ROOM_ID) return;
    setSelectedMemberIds(Array.isArray(activeRoom.members) ? activeRoom.members : []);
    setShowMemberModal(true);
  };

  const handleDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget === event.target) setIsDragging(false);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files || []).filter(
      (file) => file instanceof File && file.size > 0,
    );
    if (files.length) setMediaFiles((prev) => [...prev, ...files]);
  };

  const activeParentRooms = activeRoom
    ? visibleRooms.filter(
        (room) =>
          room.scope === activeRoom.scope && room.parentId === activeRoom.parentId,
      )
    : [];

  return (
    <div className="flex h-[90vh] overflow-hidden rounded-xl bg-gray-50 text-gray-900 shadow-xl">
      <aside className="flex w-[390px] shrink-0 flex-col border-r border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between px-3 py-3">
          <h1 className="text-2xl font-bold tracking-normal text-sm-primary">Chats</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setShowCreateRoomModal(true)}
              className="flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium text-sm-primary transition-colors hover:bg-card"
            >
              <Plus size={18} />
              Add Chatroom
            </button>
            {/* <button
              type="button"
              className="rounded-full p-2 text-sm-primary hover:bg-card"
              title="More"
            >
              <MoreVertical size={21} />
            </button> */}
          </div>
        </div>

        <div className="px-3 pb-3">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              className="h-11 w-full rounded-full border border-gray-200 bg-gray-50 pl-12 pr-4 text-sm text-gray-900 outline-none placeholder:text-gray-400 focus:border-sm-primary"
              placeholder="Search or start a new chat"
            />
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs font-semibold text-gray-600">
            <span className="rounded-full bg-card px-3 py-1.5 text-sm-primary">
              All
            </span>
            <span className="rounded-full border border-gray-200 px-3 py-1.5">
              Unread {visibleRooms.reduce((sum, room) => sum + (calculateUnread(room) > 0 ? 1 : 0), 0)}
            </span>
            {/* <span className="rounded-full border border-gray-200 px-3 py-1.5">
              Groups {visibleRooms.length}
            </span>
            <button
              type="button"
              className="ml-auto rounded-full border border-gray-200 p-1.5 hover:bg-gray-50"
              title="Filters"
            >
              <ChevronDown size={18} />
            </button> */}
          </div>
        </div>

        {/* <div className="flex items-center gap-5 px-8 py-3 text-[#aebac1]">
          <Archive size={19} />
          <span className="font-medium">Archived</span>
        </div> */}

        <div className="flex-1 overflow-y-auto px-2">
          {filteredRooms.map((room) => {
            const unread = calculateUnread(room);
            const active = room.key === activeRoomKey;
            return (
              <button
                key={room.key}
                type="button"
                onClick={() => openRoom(room)}
                className={`flex w-full items-center gap-3 px-3 py-2 mb-1 text-left rounded-lg transition ${
                  active ? "bg-card" : "hover:bg-gray-50"
                }`}
              >
                <ParentRoomStack room={room} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm text-gray-500">
                        {room.parentName}
                      </div>
                      <div className="truncate text-md font-semibold text-gray-900">
                        {getDisplayTitle(room)}
                      </div>
                    </div>
                    <span className="shrink-0 text-xs text-gray-500">
                      {formatTime(room.latestAt)}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center gap-2">
                    <p className="min-w-0 flex-1 truncate text-sm text-gray-500">
                      {room.latestMessage
                        ? `${room.latestMessage.senderName || "Member"}: ${getMessagePreview(room.latestMessage)}`
                        : "No messages yet"}
                    </p>
                    {unread > 0 && (
                      <span className="flex h-5 min-w-[20px] items-center justify-center rounded-full bg-sm-primary px-1 text-xs font-bold text-white">
                        {unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </aside>

      <main className="flex min-w-0 flex-1 flex-col bg-gray-50">
        {activeRoom ? (
          <>
            <header className="relative flex h-[70px] shrink-0 items-center justify-between border-b border-gray-200 bg-gray-100 px-4">
              <button
                type="button"
                className="flex min-w-0 items-center gap-3 text-left"
                onClick={() => setShowInfo(true)}
              >
                <RoomIcon room={activeRoom} size="h-12 w-12" />
                <div className="min-w-0">
                  <h2 className="truncate text-lg font-bold">
                    {getDisplayTitle(activeRoom)}
                  </h2>
                  <p className="truncate text-sm text-gray-500">
                    {participantIds.length
                      ? activeMembers
                          .filter((member) => participantIds.includes(member.user_id))
                          .slice(0, 4)
                          .map(memberName)
                          .join(", ")
                      : "Open to all members"}
                  </p>
                </div>
              </button>

              <div className="flex items-center gap-3">
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setParentRoomMenu((prev) => !prev)}
                    className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-gray-50"
                    title={`${activeRoom.parentName} rooms`}
                  >
                    <Avatar
                      src={activeRoom.parentImage}
                      name={activeRoom.parentName}
                      className="h-10 w-10 text-xs"
                      rounded="rounded-xl"
                      cacheKey={`${activeRoom.scope}-parent:${activeRoom.parentId}`}
                    />
                    <ChevronDown size={18} className="text-gray-500" />
                  </button>
                  {parentRoomMenu && (
                    <div className="absolute right-0 top-12 z-50 max-h-96 w-72 overflow-y-auto rounded-xl border border-gray-200 bg-white p-2 shadow-2xl">
                      {activeParentRooms.map((room) => (
                        <button
                          key={room.key}
                          type="button"
                          onClick={() => openRoom(room)}
                          className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left hover:bg-gray-50"
                        >
                          <RoomIcon room={room} size="h-9 w-9" />
                          <div className="w-[75%]">
                            <div className="truncate font-semibold">
                              {getDisplayTitle(room)}
                            </div>
                            <div className="truncate text-xs text-gray-500">
                              {getMessagePreview(room.latestMessage)}
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className="rounded-full p-2 text-sm-primary hover:bg-card"
                  onClick={() => setShowInfo(true)}
                  title="Group info"
                >
                  <Info size={20} />
                </button>
                <button
                  type="button"
                  className="rounded-full p-2 text-sm-primary hover:bg-card"
                  onClick={() => setChatSearchOpen((prev) => !prev)}
                  title="Search"
                >
                  <Search size={20} />
                </button>
                <div className="relative">
                  <button
                    type="button"
                    className="rounded-full p-2 text-sm-primary hover:bg-card"
                    onClick={() => setHeaderMenuOpen((prev) => !prev)}
                    title="Chat actions"
                  >
                    <MoreVertical size={20} />
                  </button>
                  {headerMenuOpen && (
                    <div className="absolute right-0 top-12 z-50 w-52 rounded-xl border border-gray-200 bg-white p-2 text-sm shadow-2xl">
                      {activeAdmin && activeRoom.roomId !== GENERAL_ROOM_ID && (
                        <button
                          type="button"
                          className="flex w-full items-center gap-4 rounded-xl px-3 py-2.5 text-left hover:bg-gray-50"
                          onClick={() => {
                            setHeaderMenuOpen(false);
                            openMemberModal();
                          }}
                        >
                          <UserPlus size={18} />
                          <span>Add member</span>
                        </button>
                      )}
                      <button
                        type="button"
                        className="flex w-full items-center gap-4 rounded-xl px-3 py-2.5 text-left hover:bg-gray-50"
                        onClick={() => {
                          setHeaderMenuOpen(false);
                          setShowInfo(true);
                        }}
                      >
                        <Info size={18} />
                        <span>Group info</span>
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center gap-4 rounded-xl px-3 py-2.5 text-left hover:bg-gray-50"
                        onClick={() => {
                          setHeaderMenuOpen(false);
                          setChatSearchOpen(true);
                        }}
                      >
                        <Search size={18}  />
                        <span>Search</span>
                      </button>
                      <button
                        type="button"
                        className="flex w-full items-center gap-4 rounded-xl px-3 py-2.5 text-left hover:bg-gray-50"
                        onClick={() => {
                          setHeaderMenuOpen(false);
                          setSelectionMode(true);
                        }}
                      >
                        <Check size={18}  />
                        <span>Select messages</span>
                      </button>
                      {activeAdmin && activeRoom.roomId !== GENERAL_ROOM_ID && (
                        <button
                          type="button"
                          className="flex w-full items-center gap-4 rounded-xl px-3 py-2.5 text-left text-red-600 hover:bg-gray-50"
                          onClick={deleteActiveRoom}
                        >
                          <Trash2 size={18} />
                          <span>Delete chat room</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </header>

            {chatSearchOpen && (
              <div className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4">
                <Search size={18} className="text-gray-400" />
                <input
                  value={chatSearchQuery}
                  onChange={(event) => setChatSearchQuery(event.target.value)}
                  className="h-9 flex-1 rounded-lg border border-gray-200 bg-gray-50 px-3 text-sm outline-none focus:border-sm-primary"
                  placeholder="Search in chat"
                  autoFocus
                />
                <span className="w-20 text-right text-xs text-gray-500">
                  {chatSearchQuery.trim()
                    ? `${chatSearchResults.length ? chatSearchIndex + 1 : 0}/${chatSearchResults.length}`
                    : ""}
                </span>
                <button
                  type="button"
                  onClick={() => goToChatSearchResult("prev")}
                  disabled={!chatSearchResults.length}
                  className="rounded-full p-2 text-sm-primary hover:bg-card disabled:opacity-40"
                  title="Previous result"
                >
                  <ArrowLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => goToChatSearchResult("next")}
                  disabled={!chatSearchResults.length}
                  className="rounded-full p-2 text-sm-primary hover:bg-card disabled:opacity-40"
                  title="Next result"
                >
                  <ArrowRight size={18} />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setChatSearchOpen(false);
                    setChatSearchQuery("");
                  }}
                  className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
                  title="Close search"
                >
                  <X size={18} />
                </button>
              </div>
            )}

            {selectionMode && (
              <div className="flex h-14 items-center gap-3 border-b border-gray-200 bg-white px-4">
                <button
                  type="button"
                  onClick={() => {
                    setSelectionMode(false);
                    setSelectedMessageIds([]);
                  }}
                  className="rounded-full p-2 hover:bg-gray-100"
                >
                  <X size={22} />
                </button>
                <span className="font-semibold">
                  {selectedMessageIds.length} selected
                </span>
                <div className="ml-auto flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!selectedMessages.length}
                    onClick={() => copyMessages(selectedMessages)}
                    className="rounded-full p-2 text-sm-primary hover:bg-card disabled:opacity-40"
                    title="Copy"
                  >
                    <Copy size={22} />
                  </button>
                  <button
                    type="button"
                    disabled={!selectedMessages.length}
                    onClick={() => openForwardModal(selectedMessages)}
                    className="rounded-full p-2 text-sm-primary hover:bg-card disabled:opacity-40"
                    title="Forward"
                  >
                    <Forward size={22} />
                  </button>
                  <button
                    type="button"
                    disabled={!selectedMessages.length}
                    onClick={() => deleteMessages(selectedMessages)}
                    className="rounded-full p-2 text-red-600 hover:bg-red-50 disabled:opacity-40"
                    title="Delete"
                  >
                    <Trash2 size={22} />
                  </button>
                </div>
              </div>
            )}

            <div
              ref={messagesContainerRef}
              className={`relative flex-1 overflow-y-auto py-4 ${
                isDragging ? "border-4 border-dashed border-sm-primary" : ""
              }`}
              style={{
                backgroundColor: "#F9FAFB",
                backgroundImage:
                  "radial-gradient(circle at 1px 1px, rgba(59,0,105,0.06) 1px, transparent 0)",
                backgroundSize: "28px 28px",
              }}
              onScroll={handleMessagesScroll}
              onDragEnter={handleDragEnter}
              onDragOver={(event) => event.preventDefault()}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              {isDragging && (
                <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/90">
                  <div className="rounded-xl border border-sm-primary/40 bg-white px-8 py-5 text-center shadow-xl">
                    <FileText className="mx-auto mb-3 h-10 w-10 text-sm-primary" />
                    <div className="text-lg font-bold">Drop files here</div>
                  </div>
                </div>
              )}

              {!activeRoom.messages.length ? (
                <div className="flex h-full flex-col items-center justify-center text-gray-500">
                  <img src={NoMessage} alt="No messages" className="mb-4 h-40 w-40" />
                  <div>No messages yet. Start the conversation.</div>
                </div>
              ) : (
                activeRoom.messages.map((message, index) => {
                  const previousMessage =
                    index > 0 ? activeRoom.messages[index - 1] : null;
                  const isActiveSearchHit =
                    chatSearchQuery.trim() &&
                    chatSearchResults[chatSearchIndex]?.id === message.id;
                  return (
                    <div
                      key={message.id}
                      ref={(node) => {
                        if (node) messageRefs.current[message.id] = node;
                      }}
                      className={isActiveSearchHit ? "bg-sm-accent/10" : ""}
                    >
                      {shouldShowChatDateSeparator(message, previousMessage) && (
                        <ChatDateSeparator timestamp={message.createdAt} />
                      )}
                      <ChatMessage
                        message={message}
                        previousMessage={previousMessage}
                        currentUser={currentUser}
                        members={activeMembers}
                        readBy={activeRoom.readBy}
                        participantIds={participantIds}
                        selectionMode={selectionMode}
                        selected={selectedMessageIds.includes(message.id)}
                        onToggleSelect={toggleMessageSelection}
                        menuOpenFor={menuOpenFor}
                        setMenuOpenFor={setMenuOpenFor}
                        onCopy={copyMessages}
                        onDelete={deleteMessages}
                        onEdit={startEditMessage}
                        onReply={setReplyToMessage}
                        onForward={openForwardModal}
                        onOpenGallery={openVisualGallery}
                        eagerMedia={latestMediaMessageIds.has(message.id)}
                      />
                    </div>
                  );
                })
              )}
            </div>

            {showScrollToBottom && (
              <button
                type="button"
                onClick={() => {
                  autoScrollRef.current = true;
                  forceScrollToBottomRef.current = true;
                  setShowScrollToBottom(false);
                  scrollToBottom();
                }}
                className="absolute bottom-24 right-8 z-20 rounded-full bg-white p-3 text-sm-primary shadow-xl hover:bg-card"
              >
                <ChevronDown size={22} />
              </button>
            )}

            <div className="border-t border-gray-200 bg-white text-black">
              <MessageInput
                onSend={sendChatMessage}
                replyToMessage={replyToMessage}
                setReplyToMessage={setReplyToMessage}
                teamMembers={activeMembers}
                isUploading={isUploading}
                mediaFiles={mediaFiles}
                setMediaFiles={setMediaFiles}
              />
            </div>
          </>
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-gray-500">
            <Users className="mb-4 h-12 w-12" />
            <div>Select a chatroom to start messaging</div>
          </div>
        )}
      </main>

      {showInfo && activeRoom && (
        <GroupInfoPanel
          room={activeRoom}
          members={activeMembers}
          participantIds={participantIds}
          mediaByType={mediaByType}
          activeAdmin={activeAdmin}
          onClose={() => setShowInfo(false)}
          onAddMembers={openMemberModal}
          onIconUploadClick={() => iconUploadInputRef.current?.click()}
          allMedia={allRoomMedia}
          onOpenMediaViewer={(tab = "images") => {
            setMediaViewerTab(tab);
            setMediaViewerOpen(true);
          }}
        />
      )}

      <input
        ref={iconUploadInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => handleRoomIconUpload(event.target.files?.[0])}
      />

      {showMemberModal && activeRoom && (
        <MemberModal
          room={activeRoom}
          members={activeMembers}
          selectedMemberIds={selectedMemberIds}
          setSelectedMemberIds={setSelectedMemberIds}
          onClose={() => setShowMemberModal(false)}
          onSave={saveRoomMembers}
        />
      )}

      {forwardMessages.length > 0 && (
        <ForwardModal
          rooms={visibleRooms}
          selectedKeys={forwardTargetKeys}
          setSelectedKeys={setForwardTargetKeys}
          onClose={() => {
            setForwardMessages([]);
            setForwardTargetKeys([]);
          }}
          onForward={forwardSelectedMessages}
          count={forwardMessages.length}
        />
      )}

      {showCreateRoomModal && (
        <CreateRoomModal
          projects={projects}
          organization={organization}
          scope={createRoomScope}
          setScope={setCreateRoomScope}
          parentId={createRoomParentId}
          setParentId={setCreateRoomParentId}
          title={createRoomTitle}
          setTitle={setCreateRoomTitle}
          onClose={() => setShowCreateRoomModal(false)}
          onCreate={createRoom}
        />
      )}

      {editingMessage && (
        <EditMessageModal
          text={editingText}
          setText={setEditingText}
          onClose={() => setEditingMessage(null)}
          onSave={saveEditedMessage}
        />
      )}

      {mediaViewerOpen && (
        <MediaGalleryModal
          mediaByType={mediaByType}
          initialTab={mediaViewerTab}
          onClose={() => setMediaViewerOpen(false)}
          onOpenGallery={openVisualGallery}
        />
      )}

      {zoomGallery.length > 0 && (
        <VisualMediaModal
          gallery={zoomGallery}
          index={zoomIndex}
          setIndex={setZoomIndex}
          onClose={() => {
            setZoomGallery([]);
            setZoomIndex(0);
          }}
        />
      )}

      {menuOpenFor && (
        <div className="fixed inset-0 z-30" onClick={() => setMenuOpenFor(null)} />
      )}
    </div>
  );
};

const GroupInfoPanel = ({
  room,
  members,
  participantIds,
  mediaByType,
  allMedia,
  activeAdmin,
  onClose,
  onAddMembers,
  onIconUploadClick,
  onOpenMediaViewer,
}) => {
  const visibleMembers = participantIds.length
    ? members.filter((member) => participantIds.includes(member.user_id))
    : members;
  const mediaCount = Object.values(mediaByType).reduce(
    (sum, list) => sum + list.length,
    0,
  );

  return (
    <aside className="flex w-[430px] shrink-0 flex-col border-l border-gray-200 bg-white">
      <div className="flex h-[70px] items-center justify-start gap-3 border-b border-gray-200 px-3">
        <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
          <X size={24} />
        </button>
        <h2 className="text-xl font-bold text-sm-primary">Group Info</h2>
      </div>

      <div className="flex-1 overflow-y-auto">
        <section className="flex flex-col items-center px-6 py-8 text-center">
          <div className="relative">
            <RoomIcon room={room} size="h-28 w-28" />
            {activeAdmin && room.roomId !== GENERAL_ROOM_ID && (
              <button
                type="button"
                onClick={onIconUploadClick}
                className="absolute bottom-1 right-1 rounded-full bg-sm-primary p-2 text-white shadow-lg hover:bg-sm-hover"
                title="Change chatroom icon"
              >
                <Camera size={18} />
              </button>
            )}
          </div>
          <h3 className="mt-6 text-xl font-bold">{getDisplayTitle(room)}</h3>
          <p className="mt-2 text-md text-gray-500">
            Group in {room.parentName} -{" "}
            <span className="text-sm-primary">{visibleMembers.length} members</span>
          </p>
          {activeAdmin && room.roomId !== GENERAL_ROOM_ID && (
            <button
              type="button"
              onClick={onAddMembers}
              className="mt-3 flex w-[80%] items-center justify-center gap-3 rounded-xl border border-gray-200 py-2 text-md font-semibold text-sm-primary hover:bg-card"
            >
              <UserPlus className="text-sm-primary" />
              Add
            </button>
          )}
        </section>

        <InfoRow
          avatar={
            <Avatar
              src={room.parentImage}
              name={room.parentName}
              className="h-14 w-14 text-sm"
              rounded="rounded-xl"
              cacheKey={`${room.scope}-parent:${room.parentId}`}
            />
          }
          title={room.parentName}
          subtitle={room.scope === "global" ? "Organization" : "Project"}
        />

        <button
          type="button"
          onClick={() => onOpenMediaViewer("images")}
          className="w-full border-t border-gray-200 px-6 py-3 text-left hover:bg-gray-50"
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 text-lg font-semibold">
              <ImageIcon className="text-gray-500" />
              Media, links and docs
            </div>
            <span className="text-gray-500">{mediaCount}</span>
          </div>
          <div className="mt-3 flex gap-3 overflow-hidden">
            {allMedia.slice(0, 4).map((media, index) => (
              <MediaPreviewTile
                key={`${media.messageId}-${media.mediaId}-${index}`}
                media={media}
              />
            ))}
            {!allMedia.length && (
              <div className="text-sm text-gray-500">No media shared yet</div>
            )}
          </div>
        </button>

        <section className="border-t border-gray-200 px-6 py-5">
          <div className="mb-4 text-lg font-semibold">
            {visibleMembers.length} members
          </div>
          <div className="space-y-3">
            {visibleMembers.map((member) => (
              <div key={member.user_id} className="flex items-center gap-3">
                <Avatar
                  src={member.userpic}
                  name={memberName(member)}
                  className="h-11 w-11 text-xs"
                  cacheKey={`user:${member.user_id}`}
                />
                <div className="min-w-0">
                  <div className="truncate font-semibold">{memberName(member)}</div>
                  <div className="truncate text-sm text-gray-500">
                    {member.profession || member.joined_as || member.email || "Member"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="border-t border-gray-200 px-6 py-2 text-gray-500">
          <div className="font-semibold text-gray-900">Created by</div>
          <div className="mt-2">
            {room.creatorName || "SiteMaster"} {room.createdAt ? `on ${formatDateTime(room.createdAt)}` : ""}
          </div>
        </section>
      </div>
    </aside>
  );
};

const MediaPreviewTile = ({ media }) => {
  const [mediaData, setMediaData] = useState(null);
  const [loadRef, isNearViewport] = useNearViewport();

  useEffect(() => {
    if (!isNearViewport) return undefined;
    let alive = true;
    const fetchMedia = async () => {
      if (!media?.mediaId) return;
      const data = await getCachedSnagMedia(media.mediaId);
      if (alive) setMediaData(data);
    };
    fetchMedia();
    return () => {
      alive = false;
    };
  }, [isNearViewport, media?.mediaId]);

  const type = mediaData?.type || media?.type || "";
  const url = getMediaUrl(mediaData) || media?.url;

  if (!isNearViewport) {
    return <div ref={loadRef} className="h-24 w-24 shrink-0 rounded-lg bg-gray-100" />;
  }

  if (type.includes("image") && url) {
    return (
      <img
        ref={loadRef}
        src={url}
        alt="Shared media"
        className="h-24 w-24 shrink-0 rounded-lg object-cover"
      />
    );
  }

  if ((type.includes("video") || isVideoUrl(url)) && url) {
    return (
      <video ref={loadRef} src={url} className="h-24 w-24 shrink-0 rounded-lg object-cover" />
    );
  }

  const Icon = type.includes("audio") ? Music : FileText;
  return (
    <div ref={loadRef} className="flex h-24 w-24 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-gray-500">
      <Icon size={26} />
    </div>
  );
};

const InfoRow = ({ avatar, title, subtitle }) => (
  <section className="border-t border-gray-200 px-6 py-4">
    <div className="flex items-center gap-4">
      {avatar}
      <div className="min-w-0">
        <div className="truncate text-lg font-bold">{title}</div>
        <div className="truncate text-gray-500">{subtitle}</div>
      </div>
    </div>
  </section>
);

const MemberModal = ({
  room,
  members,
  selectedMemberIds,
  setSelectedMemberIds,
  onClose,
  onSave,
}) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-md rounded-2xl bg-white text-gray-900 shadow-2xl">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <div>
          <h3 className="text-lg font-bold text-sm-primary">Add members</h3>
          <p className="text-sm text-gray-500">{getDisplayTitle(room)}</p>
        </div>
        <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
          <X size={22} />
        </button>
      </div>
      <div className="max-h-[55vh] overflow-y-auto p-3">
        {members.map((member) => {
          const checked = selectedMemberIds.includes(member.user_id);
          return (
            <label
              key={member.user_id}
              className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-3 hover:bg-gray-50"
            >
              <input
                type="checkbox"
                checked={checked}
                onChange={() =>
                  setSelectedMemberIds((prev) =>
                    checked
                      ? prev.filter((id) => id !== member.user_id)
                      : [...prev, member.user_id],
                  )
                }
              />
              <Avatar
                src={member.userpic}
                name={memberName(member)}
                className="h-10 w-10 text-xs"
                cacheKey={`user:${member.user_id}`}
              />
              <div>
                <div className="font-semibold">{memberName(member)}</div>
                <div className="text-xs text-gray-500">
                  {member.profession || member.joined_as || "Member"}
                </div>
              </div>
            </label>
          );
        })}
      </div>
      <div className="flex justify-between border-t border-gray-200 px-5 py-4">
        <div className="flex gap-3 text-sm text-gray-500">
          <button type="button" onClick={() => setSelectedMemberIds(members.map((m) => m.user_id))}>
            Select all
          </button>
          <button type="button" onClick={() => setSelectedMemberIds([])}>
            Clear
          </button>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 hover:bg-gray-100">
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            className="rounded-lg bg-sm-primary px-4 py-2 font-semibold text-white"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </div>
);

const CreateRoomModal = ({
  projects,
  organization,
  scope,
  setScope,
  parentId,
  setParentId,
  title,
  setTitle,
  onClose,
  onCreate,
}) => {
  const parentOptions =
    scope === "global"
      ? [{ id: organization?.id, name: organization?.organization_name || "Global" }]
      : projects.map((project) => ({
          id: project.id,
          name: project.site_name || `Project ${project.id}`,
        }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-xl bg-white  text-gray-900 shadow-2xl">
        <div className="mb-3 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-sm-primary">New chatroom</h3>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
            <X size={22} />
          </button>
        </div>

        <div className="mb-1 grid grid-cols-2 gap-2 px-4 rounded-lg p-1">
          {[
            { value: "global", label: "Global" },
            { value: "site", label: "Project" },
          ].map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                setScope(option.value);
                setParentId(
                  option.value === "global"
                    ? String(organization?.id || "")
                    : String(projects[0]?.id || ""),
                );
              }}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${
                scope === option.value
                  ? "bg-white text-sm-primary shadow"
                  : "text-gray-500 hover:text-gray-900"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <label className="mb-4 block px-4 py-3">
          <span className="mb-1 block text-sm font-semibold text-gray-700">
            {scope === "global" ? "Organization" : "Project"}
          </span>
          <select
            value={parentId}
            onChange={(event) => setParentId(event.target.value)}
            className="h-11 w-full rounded-lg border border-gray-200 bg-white px-3 outline-none focus:border-sm-primary"
          >
            {parentOptions.map((option) => (
              <option key={option.id} value={option.id}>
                {option.name}
              </option>
            ))}
          </select>
        </label>

        <label className="block px-4 py-3">
          <span className="mb-1 block text-sm font-semibold text-gray-700">
            Chatroom name
          </span>
          <input
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            className="h-11 w-full rounded-lg border border-gray-200 px-3 outline-none focus:border-sm-primary"
            placeholder="Procurement, Site Issues, Design..."
            autoFocus
          />
        </label>

        <div className="mt-6 px-4 py-3 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 hover:bg-gray-100">
            Cancel
          </button>
          <button
            type="button"
            onClick={onCreate}
            disabled={!title.trim() || !parentId}
            className="rounded-lg bg-sm-primary px-4 py-2 font-semibold text-white disabled:opacity-40"
          >
            Create
          </button>
        </div>
      </div>
    </div>
  );
};

const EditMessageModal = ({ text, setText, onClose, onSave }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
    <div className="w-full max-w-xl rounded-2xl bg-white p-5 text-gray-900 shadow-2xl">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-sm-primary">Edit message</h3>
        <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
          <X size={22} />
        </button>
      </div>
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        className="h-40 w-full resize-none rounded-lg border border-gray-200 p-3 outline-none focus:border-sm-primary"
      />
      <div className="mt-4 flex justify-end gap-2">
        <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 hover:bg-gray-100">
          Cancel
        </button>
        <button
          type="button"
          onClick={onSave}
          className="rounded-lg bg-sm-primary px-4 py-2 font-semibold text-white"
        >
          Save
        </button>
      </div>
    </div>
  </div>
);

const MediaGalleryItem = ({ media, allVisuals, visualIndex, onOpenGallery }) => {
  const [mediaData, setMediaData] = useState(null);
  const [loadRef, isNearViewport] = useNearViewport();

  useEffect(() => {
    if (!isNearViewport) return undefined;
    let alive = true;
    const fetchMedia = async () => {
      if (!media?.mediaId) return;
      const data = await getCachedSnagMedia(media.mediaId);
      if (alive) setMediaData(data);
    };
    fetchMedia();
    return () => {
      alive = false;
    };
  }, [isNearViewport, media?.mediaId]);

  const type = mediaData?.type || media?.type || "";
  const url = getMediaUrl(mediaData) || media?.url;

  if (!isNearViewport) {
    return <div ref={loadRef} className="aspect-square rounded-lg bg-gray-100" />;
  }

  if (isVisualMedia({ type }) && url) {
    return (
      <button
        ref={loadRef}
        type="button"
        onClick={() => onOpenGallery(allVisuals, visualIndex)}
        className="aspect-square overflow-hidden rounded-lg bg-gray-100"
      >
        {type.includes("video") || isVideoUrl(url) ? (
          <video src={url} className="h-full w-full object-cover" />
        ) : (
          <img src={url} alt="Shared media" className="h-full w-full object-cover" />
        )}
      </button>
    );
  }

  return (
    <div ref={loadRef} className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <MediaItem
        mediaItem={media}
        setShowImagePreview={() => {}}
        setSelectedMedia={() => {}}
      />
    </div>
  );
};

const MediaGalleryModal = ({ mediaByType, initialTab, onClose, onOpenGallery }) => {
  const [tab, setTab] = useState(initialTab);
  const visualMedia = [...mediaByType.images, ...mediaByType.videos];
  const tabItems = {
    media: visualMedia,
    documents: mediaByType.documents,
    audio: mediaByType.audio,
  };

  useEffect(() => {
    if (initialTab === "documents") setTab("documents");
    else if (initialTab === "audio") setTab("audio");
    else setTab("media");
  }, [initialTab]);

  const currentItems = tabItems[tab] || [];

  return (
    <div className="fixed inset-0 z-50 flex justify-center items-center bg-black/40">
      <div className="h-[80%] w-full max-w-3xl overflow-hidden bg-white rounded-lg text-gray-900 shadow-2xl">
        <div className="flex h-16 items-center gap-4 border-b border-gray-200 px-3">
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
            <ArrowLeft size={24} />
          </button>
          <h3 className="text-xl font-bold text-sm-primary">Media, links and docs</h3>
        </div>
        <div className="grid grid-cols-3 border-b border-gray-200 text-center text-md font-semibold">
          {[
            { key: "media", label: "Media" },
            { key: "documents", label: "Docs" },
            { key: "audio", label: "Audio" },
          ].map((item) => (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`py-2 ${
                tab === item.key
                  ? "border-b-[3px] border-sm-primary text-sm-primary"
                  : "text-gray-500"
              }`}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="h-[calc(100%-128px)] overflow-y-auto p-6">
          <div className="mb-4 text-sm font-bold uppercase text-gray-700">This month</div>
          {currentItems.length ? (
            <div className="grid grid-cols-4 gap-3">
              {currentItems.map((media, index) => (
                <MediaGalleryItem
                  key={`${media.messageId}-${media.mediaId}-${index}`}
                  media={media}
                  allVisuals={visualMedia}
                  visualIndex={visualMedia.findIndex((item) => item === media)}
                  onOpenGallery={onOpenGallery}
                />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center text-gray-500">No items here yet.</div>
          )}
        </div>
      </div>
    </div>
  );
};

const VisualMediaModal = ({ gallery, index, setIndex, onClose }) => {
  const current = gallery[index];
  const hasMany = gallery.length > 1;

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowRight" && hasMany) {
        setIndex((prev) => (prev + 1) % gallery.length);
      }
      if (event.key === "ArrowLeft" && hasMany) {
        setIndex((prev) => (prev - 1 + gallery.length) % gallery.length);
      }
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [gallery.length, hasMany, onClose, setIndex]);

  if (!current) return null;

  if (current.type?.includes("video") || isVideoUrl(current.url)) {
    return (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
        >
          <X size={24} />
        </button>
        {hasMany && (
          <>
            <button
              type="button"
              onClick={() => setIndex((prev) => (prev - 1 + gallery.length) % gallery.length)}
              className="absolute left-5 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"
            >
              <ArrowLeft size={26} />
            </button>
            <button
              type="button"
              onClick={() => setIndex((prev) => (prev + 1) % gallery.length)}
              className="absolute right-5 rounded-full bg-white/10 p-3 text-white hover:bg-white/20"
            >
              <ArrowRight size={26} />
            </button>
          </>
        )}
        <video src={current.url} controls autoPlay className="max-h-[86vh] max-w-[86vw]" />
      </div>
    );
  }

  return (
    <>
      <ImageZoomModal
        isOpen
        onClose={onClose}
        imageUrl={current.url}
        itemName={`Media ${index + 1} of ${gallery.length}`}
      />
      {hasMany && (
        <div className="pointer-events-none fixed inset-0 z-[10000] flex items-center justify-between px-6">
          <button
            type="button"
            onClick={() => setIndex((prev) => (prev - 1 + gallery.length) % gallery.length)}
            className="pointer-events-auto rounded-full bg-white/10 p-3 text-white hover:bg-white/20"
          >
            <ArrowLeft size={26} />
          </button>
          <button
            type="button"
            onClick={() => setIndex((prev) => (prev + 1) % gallery.length)}
            className="pointer-events-auto rounded-full bg-white/10 p-3 text-white hover:bg-white/20"
          >
            <ArrowRight size={26} />
          </button>
        </div>
      )}
    </>
  );
};

const ForwardModal = ({
  rooms,
  selectedKeys,
  setSelectedKeys,
  onClose,
  onForward,
  count,
}) => {
  const [query, setQuery] = useState("");
  const filteredRooms = rooms.filter((room) =>
    `${room.parentName} ${getDisplayTitle(room)}`
      .toLowerCase()
      .includes(query.toLowerCase()),
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white text-gray-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-lg font-bold text-sm-primary">Forward message</h3>
            <p className="text-sm text-gray-500">
              {count} message{count === 1 ? "" : "s"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full p-2 hover:bg-gray-100">
            <X size={22} />
          </button>
        </div>
        <div className="p-4">
          <div className="relative mb-3">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="h-11 w-full rounded-full border border-gray-200 bg-gray-50 pl-12 pr-4 text-sm text-gray-900 outline-none focus:border-sm-primary"
              placeholder="Search chats"
            />
          </div>
          <div className="max-h-[55vh] overflow-y-auto">
            {filteredRooms.map((room) => {
              const checked = selectedKeys.includes(room.key);
              return (
                <label
                  key={room.key}
                  className="flex cursor-pointer items-center gap-3 rounded-xl px-3 py-3 hover:bg-gray-50"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      setSelectedKeys((prev) =>
                        checked
                          ? prev.filter((key) => key !== room.key)
                          : [...prev, room.key],
                      )
                    }
                  />
                  <ParentRoomStack room={room} />
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{getDisplayTitle(room)}</div>
                    <div className="truncate text-sm text-gray-500">
                      {room.parentName}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-gray-200 px-5 py-4">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 hover:bg-gray-100">
            Cancel
          </button>
          <button
            type="button"
            onClick={onForward}
            disabled={!selectedKeys.length}
            className="rounded-lg bg-sm-primary px-4 py-2 font-semibold text-white disabled:opacity-40"
          >
            Forward
          </button>
        </div>
      </div>
    </div>
  );
};

export default Chats;
